import { useEffect, useState } from 'react';

import { Icon } from '@rbilabs/universal-components';
import { usePaymentContext } from 'features/payment';
import { IPaymentMethod } from 'features/payment/types';
import { View } from 'react-native';

import ActionButton, { ActionButtonVariants } from 'components/action-button';
import { ServiceMode } from 'generated/graphql-gateway';
import { MastercardPartnershipAlert } from 'pages/loyalty/loyalty-partners/mastercard/mastercard-partnership-alert';
import { t } from 'state/intl/translations';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { spacing } from 'styles/constants';

import { WidgetBaseStyles } from '../../../checkout/pages/styled';

import { SelectorAvailablePayments } from './selector-available-payments';
import { SelectorBottomSheet } from './selector-bottom-sheet';
import { SelectorLoading } from './selector-loading';
import { SelectorPrepaidReload } from './selector-prepaid-reload';
import { SelectorSelection } from './selector-selection';
import { SelectionType, SelectorProps } from './types';

export type PaymentMethodSelectorProps = SelectorProps & {
  isLoading?: boolean;
  isDisabled?: boolean;
  noPaymentMethodsAvailable?: boolean;
  serviceMode: ServiceMode;
  prepaid?: {
    allowedMethodsForReload: IPaymentMethod[];
    shouldReloadPrepaidMethod: boolean;
    reloadAmount: number;
    selectedMethodId: string | undefined;
    prepaidBalance?: number;
    orderTotal?: number;
  };
  onSelect: (type: SelectionType, id?: string) => void;
};
export function PaymentMethodSelector({
  methods,
  selectedMethodId,
  isLoading,
  isDisabled,
  noPaymentMethodsAvailable,
  prepaid,
  onSelect,
}: PaymentMethodSelectorProps) {
  const { clearPaymentDetails } = usePaymentContext();
  const [isSelectorOpen, setIsSelectorOpen] = useState(false);
  const [isPrepaidReload, setIsPrepaidReload] = useState(false);
  const showMastercardPartnership = useFlag(LaunchDarklyFlag.SHOW_MASTERCARD_PARTNERSHIP);

  const {
    shouldReloadPrepaidMethod = false,
    reloadAmount = 0,
    selectedMethodId: selectedReloadPaymentMethodId,
    allowedMethodsForReload: reloadPaymentMethods = [],
    prepaidBalance,
    orderTotal,
  } = prepaid || {};

  const shouldShowLoadingState = isLoading || (!noPaymentMethodsAvailable && methods.length === 0);

  const handleCloseSelector = () => {
    setIsSelectorOpen(false);
    // allows the dialog to dismiss before changing the content back to the default selector
    setTimeout(() => {
      setIsPrepaidReload(false);
    }, 100);
  };

  const handleSelection = (type: SelectionType, id?: string) => {
    onSelect?.(type, isPrepaidReload ? `prepaid-${id}` : id);
    handleCloseSelector();
  };

  const handleOnChangePaymentMethod = () => {
    setIsPrepaidReload(false);
    setIsSelectorOpen(true);
  };

  const handleOnChangePrepaidMethod = () => {
    setIsPrepaidReload(true);
    setIsSelectorOpen(true);
  };

  useEffect(() => {
    return () => {
      clearPaymentDetails();
    };
  }, [clearPaymentDetails]);

  if (shouldShowLoadingState) {
    return <SelectorLoading />;
  }

  return (
    <>
      <View>
        <View
          style={[
            WidgetBaseStyles.container,
            {
              flexDirection: 'column',
              borderBottomLeftRadius: 0,
              borderBottomRightRadius: 0,
              justifyContent: 'flex-start',
              alignItems: 'stretch',
            },
          ]}
        >
          {noPaymentMethodsAvailable ? (
            <ActionButton
              testID="add-payment-btn"
              width="100%"
              height="$10"
              marginY="$6"
              _text={{ fontSize: 'sm' }}
              variant={ActionButtonVariants.OUTLINE}
              leftIcon={<Icon variant="add" aria-hidden />}
              onPress={() => onSelect('add')}
            >
              {t('addPayment')}
            </ActionButton>
          ) : (
            <SelectorSelection
              methods={methods}
              selectedMethodId={selectedMethodId}
              isDisabled={isDisabled}
              onChange={handleOnChangePaymentMethod}
            />
          )}
          {shouldReloadPrepaidMethod && (
            <SelectorPrepaidReload
              methods={reloadPaymentMethods}
              selectedMethodId={selectedReloadPaymentMethodId}
              reloadAmount={reloadAmount}
              onChange={handleOnChangePrepaidMethod}
              prepaidBalance={prepaidBalance}
              orderTotal={orderTotal}
            />
          )}
          {showMastercardPartnership && (
            <MastercardPartnershipAlert style={{ marginTop: spacing.$4 }} />
          )}
        </View>
        {/* This display the bottom banner with the available methods as icons */}
        <SelectorAvailablePayments
          paymentMethods={methods}
          isReloadPrepaidBeingShown={!!prepaid?.shouldReloadPrepaidMethod}
        />
      </View>
      <SelectorBottomSheet
        isOpen={isSelectorOpen}
        isPrepaidReload={isPrepaidReload}
        methods={isPrepaidReload ? reloadPaymentMethods : methods}
        selectedMethodId={isPrepaidReload ? selectedReloadPaymentMethodId : selectedMethodId}
        onClose={handleCloseSelector}
        onSelect={handleSelection}
      />
    </>
  );
}
